// src/calendario/MiCalendario.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import {
  format,
  parse,
  startOfWeek as dfnsStartOfWeek,
  getDay,
  addMonths,
  subMonths,
} from "date-fns";
import esES from "date-fns/locale/es";

import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ===== MUI =====
import { Box, Paper, Typography, Stack } from "@mui/material";

/* ========= Helpers locales (sin UTC) ========= */
function startOfDayLocal(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDayLocal(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/* ========= Localizer correcto (firma startOfWeek con date) ========= */
const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) =>
    format(date, formatStr, { ...(options || {}), locale: esES }),
  parse: (value, formatStr, options) =>
    parse(value, formatStr, new Date(), { ...(options || {}), locale: esES }),
  startOfWeek: (date, options) =>
    dfnsStartOfWeek(date, { weekStartsOn: 1, locale: esES, ...(options || {}) }),
  getDay,
  locales: { es: esES },
});

const CustomEvent = ({ event }) => (
  <div>
    <div style={{ fontWeight: 700 }}>{event.title}</div>
    {event.descripcion && (
      <div style={{ fontSize: "0.9em", fontWeight: 400 }}>{event.descripcion}</div>
    )}
  </div>
);

export const MiCalendario = () => {
  const { auth } = useAuth();
  const calendarRef = useRef();

  const [plantillas, setPlantillas] = useState([]);
  const [todosLosTurnos, setTodosLosTurnos] = useState([]);
  const [eventos, setEventos] = useState([]);

  const [vistaActual, setVistaActual] = useState("week");
  const [rangoVisible, setRangoVisible] = useState(null);

  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");

  // Ventana corredera (igual que global/admin)
  const ahora = new Date();
  const [rangoCargado, setRangoCargado] = useState(() => {
    const desde = subMonths(new Date(ahora.getFullYear(), ahora.getMonth(), 1), 3);
    const hasta = addMonths(new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0), 4);
    return { desde: startOfDayLocal(desde), hasta: endOfDayLocal(hasta) };
  });

  // ===== Carga plantillas =====
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${Global.url}turnos/plantillas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json().catch(() => []);
        setPlantillas(Array.isArray(data) ? data : []);
      } catch {
        setPlantillas([]);
      }
    })();
  }, []);

  const fetchTurnos = (desde, hasta) => {
    if (!auth?.id) return;

    setLoading(true);
    setMensaje("");

    const ymdDesde = startOfDayLocal(desde).toISOString().slice(0, 10);
    const ymdHasta = endOfDayLocal(hasta).toISOString().slice(0, 10);

    fetch(
      `${Global.url}turnos?usuario_id=${auth.id}&desde=${ymdDesde}&hasta=${ymdHasta}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        const turnos = (Array.isArray(data) ? data : []).map((turno) => ({
          id: turno.id,
          title:
            turno.titulo ||
            (turno.tipo === "ausencia"
              ? turno.descripcion || "Ausencia"
              : "Turno laboral"),
          start: new Date(String(turno.inicio).replace(" ", "T")),
          end: new Date(String(turno.fin).replace(" ", "T")),
          allDay: false,
          activo: turno.activo,
          tipo: turno.tipo,
          descripcion: turno.descripcion,
          plantilla_id: turno.plantilla_id ?? null,
        }));

        setTodosLosTurnos(turnos);
        setRangoCargado({ desde: startOfDayLocal(desde), hasta: endOfDayLocal(hasta) });
      })
      .catch(() => setMensaje("Error de conexión"))
      .finally(() => setLoading(false));
  };

  // Init: rango visible del mes actual + fetch ventana
  useEffect(() => {
    if (!auth?.id) return;

    const desde = startOfDayLocal(new Date(ahora.getFullYear(), ahora.getMonth(), 1));
    const hasta = endOfDayLocal(new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0));

    setRangoVisible({ desde, hasta });
    fetchTurnos(rangoCargado.desde, rangoCargado.hasta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  // Ajusta rango visible al cambiar a "day"
  useEffect(() => {
    if (vistaActual === "day") {
      const visibleDate = calendarRef.current?.state?.currentDate || new Date();
      setRangoVisible({
        desde: startOfDayLocal(visibleDate),
        hasta: endOfDayLocal(visibleDate),
      });
    }
  }, [vistaActual]);

  // Eventos en rango visible
  useLayoutEffect(() => {
    if (!rangoVisible?.desde || !rangoVisible?.hasta) return;
    setEventos(
      (todosLosTurnos || []).filter(
        (evt) => evt.start <= rangoVisible.hasta && evt.end >= rangoVisible.desde
      )
    );
  }, [rangoVisible, todosLosTurnos]);

  const handleRangeChange = (range) => {
    let desde, hasta;

    if (Array.isArray(range)) {
      desde = startOfDayLocal(range[0]);
      hasta = endOfDayLocal(range[range.length - 1]);
    } else if (range instanceof Date) {
      desde = startOfDayLocal(range);
      hasta = endOfDayLocal(range);
    } else if (range?.start && range?.end) {
      desde = startOfDayLocal(range.start);
      hasta = endOfDayLocal(range.end);
    } else {
      const hoy = new Date();
      desde = startOfDayLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
      hasta = endOfDayLocal(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
    }

    setRangoVisible({ desde, hasta });

    if (desde < rangoCargado.desde || hasta > rangoCargado.hasta) {
      const nuevoCentro = new Date(desde.getFullYear(), desde.getMonth(), 1);
      const nuevoDesde = startOfDayLocal(subMonths(nuevoCentro, 3));
      const nuevoHasta = endOfDayLocal(
        addMonths(new Date(nuevoCentro.getFullYear(), nuevoCentro.getMonth() + 1, 0), 4)
      );
      fetchTurnos(nuevoDesde, nuevoHasta);
    }
  };

  const plantillasById = useMemo(() => {
    const m = new Map();
    (plantillas || []).forEach((p) => m.set(String(p.id), p));
    return m;
  }, [plantillas]);

  const eventPropGetter = (event) => {
    const estilo = {};

    if (event.plantilla_id) {
      const plantilla = plantillasById.get(String(event.plantilla_id));
      if (plantilla?.color) {
        estilo.backgroundColor = plantilla.color;
        estilo.color = "#222";
      }
    }

    if (event.tipo === "ausencia") {
      estilo.backgroundColor = "#ffbaba";
      estilo.color = "#fff";
    }

    if (!event.activo) {
      estilo.backgroundColor = "#ddd";
      estilo.color = "#888";
      estilo.textDecoration = "line-through";
    }

    if (!estilo.backgroundColor) {
      estilo.backgroundColor = "#85C1E9";
      estilo.color = "#222";
    }

    estilo.borderRadius = 1;
    return { style: estilo };
  };

  const minTime = new Date(2000, 0, 1, 0, 0, 0, 0);
  const maxTime = new Date(2000, 0, 1, 23, 59, 59, 999);

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Mis turnos
        </Typography>

        {mensaje && (
          <Typography color="error" sx={{ fontWeight: 600 }}>
            {mensaje}
          </Typography>
        )}

        <Paper
          sx={{
            p: 1,
            height: 620,
            overflow: "hidden",

            // Toolbar compacta (como el resto)
            "& .rbc-toolbar": {
              padding: "0px 6px",
              marginBottom: "6px",
              minHeight: 36,
            },
            "& .rbc-toolbar-label": {
              padding: 0,
              lineHeight: "36px",
              fontSize: 14,
            },
            "& .rbc-btn-group button": {
              padding: "4px 10px",
              lineHeight: 1.2,
              minHeight: 32,
            },
            "& .rbc-btn-group": {
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
            },
          }}
        >
          <Box sx={{ height: "100%" }}>
            <Calendar
              ref={calendarRef}
              localizer={localizer}
              events={eventos}
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              defaultView="week"
              views={["day", "week", "month", "agenda"]}
              onView={setVistaActual}
              onRangeChange={handleRangeChange}
              scrollToTime={new Date(2000, 0, 1, 7, 30)}
              min={minTime}
              max={maxTime}
              step={15}
              timeslots={4}
              style={{ height: "100%" }}
              eventPropGetter={eventPropGetter}
              components={{ event: CustomEvent }}
              selectable={false}
              messages={{
                date: "Fecha",
                time: "Hora",
                event: "Evento",
                allDay: "Todo el día",
                week: "Semana",
                work_week: "Semana laboral",
                day: "Día",
                month: "Mes",
                previous: "Ant",
                next: "Sig",
                yesterday: "Ayer",
                tomorrow: "Mañana",
                today: "Hoy",
                agenda: "Agenda",
                showMore: (total) => `+ Ver ${total} más`,
              }}
            />
          </Box>
        </Paper>

        <Typography sx={{ color: "text.secondary" }}>
          {loading ? "Cargando turnos…" : `${eventos.length} turno(s) en vista.`}
        </Typography>
      </Stack>
    </Box>
  );
};
